import React, { useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import axios from "axios";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import StoreSearch from "../../../Campaign/StoreSearch.js";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../../JobBatch.module.css";
import ModalToUpdateBatches from "../../ModalToUpdateBatches";
import { MoreVertOutlined } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RejectModal from "../../../../../components/AdminportalFolder/Campaign/RejectModal.jsx";
import useRejectModal from "../../../Campaign/useRejectModal.js";

const NotAccepted = ({
  // isAllBatchesLoading,
  id,
  isNotAccepted,
  matchingJobs,
  setTotalNotAcceptedBatches,
  setAvailableBatches,
  fetchAllJobsBasedOnCampaignId,
  setIsNotAccepted, // Add this prop
  setAcceptedBatches, // Add this prop
  rejectJob,
  showSnackbar,
  fetchBatchById,
}) => {
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [formattedBatches, setFormattedBatches] = useState([]);
  const [openModalToUpdateBatch, setOpenModalToUpdateBatch] =
    React.useState(false);
  const [detailsOfBatchSelected, setDetailsOfBatchSelected] = React.useState();
  const [unAcceptedBatches, setUnAcceptedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [jobToReject, setJobToReject] = useState(null);
  const batchRefs = useRef({});
  const containerRef = useRef(null);

  const {
    isProcessing: isRejectProcessing,
    rejectDialogOpen,
    itemInfo,
    openRejectModal,
    closeRejectModal,
    handleRejectConfirm,
  } = useRejectModal({
    onReject: async (ids, reason) => {

      const jobId = ids[0];
      const job = jobToReject;

      let affectedBatchId = null;
      unAcceptedBatches?.forEach((batch) => {
        if (batch.jobs?.some((j) => j?._id === jobId)) {
          affectedBatchId = batch?._id;
        }
      });

      setUnAcceptedBatches((prevBatches) =>
        prevBatches?.map((batch) => {
          if (batch?._id === affectedBatchId) {
            return {
              ...batch,
              jobs: batch?.jobs?.map((j) => {
                if (j?._id === jobId) {
                  return {
                    ...j,
                    status: 2,
                    rejectionReason: reason,
                  };
                }
                return j;
              }),
            };
          }
          return batch;
        })
      );

      // Step 3: Call the API to reject the job
      await rejectJob(jobId, reason);

      if (affectedBatchId) {
        const updatedBatch = await fetchBatchById(affectedBatchId);

        if (updatedBatch) {
          setUnAcceptedBatches((prevBatches) =>
            prevBatches?.map((batch) => {
              if (batch?._id === affectedBatchId) {
                const currentBatch = prevBatches?.find(b => b?._id === affectedBatchId);
                return {
                  ...updatedBatch,
                  dateRange: currentBatch?.dateRange || "No Dates Available",
                  totalPayment: updatedBatch?.jobs?.reduce(
                    (sum, job) => sum + (job?.paymentAmount || 0),
                    0
                  ) || 0,
                };
              }
              return batch;
            })
          );

          setTotalNotAcceptedBatches(unAcceptedBatches?.length);
        }
      }

      setJobToReject(null);
      handleCloseMenu();
    },
    showSnackbar,
  });

  const handleOpenRejectModal = (job) => {
    setJobToReject(job);
    openRejectModal(job, job?.storeName || "Job");
    handleCloseMenu();
  };

  const fetchUnAcceptedBatchBasedOnCampaignId = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Token not found in localStorage");
        setUnAcceptedBatches([]);
        setTotalNotAcceptedBatches(0);
        return;
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_URL
        }/jobs/unacceptedBatchesForRecce?campaignId=${id}&serviceType=${0}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data?.success && Array.isArray(data.data)) {
        // Add dateRange and totalPayment to each batch
        const updatedBatches = data.data.map((batch) => {
          let totalPayment = 0;
          if (batch.jobs && batch.jobs.length > 0) {
            totalPayment = batch.jobs.reduce(
              (sum, job) => sum + (job.paymentAmount || 0),
              0,
            );
            const jobDates = batch.jobs.map(
              (job) => new Date(job.jobStartingDate),
            );
            const completionDates = batch.jobs.map(
              (job) => new Date(job.jobCompletionDate),
            );
            const earliestDate = new Date(
              Math.min(...jobDates.map((date) => date.getTime())),
            );
            const latestDate = new Date(
              Math.max(...completionDates.map((date) => date.getTime())),
            );
            batch.dateRange = `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;
          } else {
            batch.dateRange = "No Dates Available";
          }
          batch.totalPayment = totalPayment;
          return batch;
        });

        setUnAcceptedBatches(updatedBatches);
        setTotalNotAcceptedBatches(updatedBatches.length);
      } else {
        console.warn("Unexpected API response", data);
        setUnAcceptedBatches([]);
        setTotalNotAcceptedBatches(0);
      }
    } catch (error) {
      console.error("Error while fetching jobs", error);
      setUnAcceptedBatches([]);
      setTotalNotAcceptedBatches(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnAcceptedBatchBasedOnCampaignId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAccordionToggle = (batchTitle) => (event) => {
    event.stopPropagation();
    setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
  };

  // Function to handle batch updates
  const handleBatchUpdate = (updatedBatch) => {
    // Update the formattedBatches state
    setFormattedBatches((prev) =>
      prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
    );

    // Update the detailsOfBatchSelected if it's the same batch
    if (detailsOfBatchSelected?._id === updatedBatch._id) {
      setDetailsOfBatchSelected(updatedBatch);
    }

    // Update the parent state (isNotAccepted)
    setIsNotAccepted((prev) =>
      prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
    );

    // If the batch now has an IP, it should be moved to accepted batches
    if (updatedBatch.installationPartnerId) {
      setIsNotAccepted((prev) =>
        prev.filter((b) => b._id !== updatedBatch._id),
      );
      setAcceptedBatches((prev) => [...prev, updatedBatch]);
    }
  };
  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };
  const approveCancellation = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/cancelJobRequestedByIP`,
        { jobId: selectedJob._id, approvedBy: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        console.log("Success - refreshing data...");
        await fetchUnAcceptedBatchBasedOnCampaignId();
        handleCloseMenu();
        if (showSnackbar) {
          showSnackbar({
            open: true,
            message: "Cancellation approved successfully",
            severity: "success",
          });
        }
      } else {
        console.error("API returned success:false", response.data);
        if (showSnackbar) {
          showSnackbar({
            open: true,
            message: response.data.message || "Failed to approve cancellation",
            severity: "error",
          });
        }
      }
      return response.data.data; // Return the updated job data
    } catch (error) {
      console.error("Error approving cancellation:", error);
      if (showSnackbar) {
        showSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to approve cancellation",
          severity: "error",
        });
      }
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const searchData = useMemo(() => {
    const result = [];

    unAcceptedBatches.forEach((batch) => {
      batch.jobs?.forEach((job) => {
        if (job.status === 2 || job.status === "2") return;
        result.push({
          id: job._id,
          storeName: job.storeName,

          batchTitle: batch.batchTitle,

          targetId: batch._id,

          subText: `Batch: ${batch.batchTitle}`,
        });
      });
    });

    return result;
  }, [unAcceptedBatches]);

  return (
    <Grid ref={containerRef} className={`${styles.scrollableContainer}`}>
      <div
        className="mx-3 py-2 sticky top-0 z-20 bg-white"

      >
        <StoreSearch
          data={searchData}
          searchKey="storeName"
          placeholder="Search Store Name"
          containerRef={containerRef}
          targetRefs={batchRefs}
          onSelect={(item) => {
            setExpandedBatch(item.batchTitle);
          }}
        />
      </div>
      {loading ? (
        <div className={styles.noJobsStyle}>
          <CircularProgress />
        </div>
      ) : unAcceptedBatches.length === 0 ? (
        <div className={styles.noJobsStyle}>
          <Typography
            className={`${styles.noJobsBox} ${styles.noJobsText}`}
            variant="h6"
            align="center"
          >
            No batches Available
          </Typography>
        </div>
      ) : (
        unAcceptedBatches.map((batch, index) => {
          return (
            <Grid
              ref={(el) => (batchRefs.current[batch._id] = el)}
              item
              xs={12}
              md={12}
              key={index}
              my={1}
              mx={2}
            >
              <Accordion expanded={expandedBatch === batch.batchTitle}>
                <AccordionSummary
                  expandIcon={
                    <IconButton
                      onClick={handleAccordionToggle(batch.batchTitle)}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  }
                  aria-controls={`panel-${index}-content`}
                  id={`panel-${index}-header`}
                >
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white w-full mx-auto">
                    <div className="flex">
                      <h1 className={`${styles.batchTitle}`}>
                        {batch.batchTitle}
                      </h1>
                    </div>
                    <div className={styles.chipContainer}>
                      <div>
                        <Chip
                          label={`Timeline: ${moment(
                            batch?.jobStartingDate,
                          ).format("DD MMM YY")} - ${moment(
                            batch?.jobCompletionDate,
                          ).format("DD MMM YY")}`}
                          className={styles.chip}
                        />
                      </div>
                      <Chip
                        label={
                          <span>Batch Payment: ₹{batch?.totalPayment}</span>
                        }
                        className={styles.chips}
                      />

                      <Chip
                        label={"Jobs:" + (batch.jobs ? batch.jobs.length : 0)}
                        className={styles.chip}
                      />
                      {/* Ellipsis Button */}
                      <button
                        className="text-gray-400 hover:text-gray-600 text-xl rounded-full px-4 py-1 hover:bg-gray-100"
                        onClick={() => {
                          setOpenModalToUpdateBatch(true);
                          setDetailsOfBatchSelected(batch);
                        }}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                </AccordionSummary>

                <AccordionDetails className={styles.accordionDetails}>
                  <Box className={`${styles.scrollableBox}`}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow className={styles.tableHeader}>
                            <TableCell className={styles.tableCell}>
                              ADDRESS
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              STORE MANAGER
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              PRODUCT TYPE
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              JOB PAY
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              ACTION
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batch.jobs.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  <span>{row.storeName}</span>
                                </div>
                                <div className={styles.address2}>
                                  <span>{row?.address}</span>,
                                  <span>{row.city}</span>,{" "}
                                  <span>{row.state}</span>,{" "}
                                  <span>{row.zipCode}</span>
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.storeOwnerName}
                                </div>
                                <div className={styles.storeManagerContact}>
                                  {row.storeOwnerPhoneNumber}
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.productId?.productType || "N/A"}
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  ₹{row.paymentAmount}
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                {row.status != 2 ? (
                                  <Tooltip title="Actions">
                                    <IconButton
                                      onClick={(event) => {
                                        handleMenuClick(event, row);
                                      }}
                                      disabled={isProcessing || isRejectProcessing}
                                      sx={{
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                          "& .MuiSvgIcon-root": {
                                            color: (theme) =>
                                              theme.palette.action.disabled,
                                          },
                                        },
                                      }}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="No actions available">
                                    <span>
                                      <IconButton disabled>
                                        <MoreVertIcon color="disabled" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })
      )}

      {openModalToUpdateBatch && (
        <ModalToUpdateBatches
          fetchUpdatedBatchDetails={fetchUnAcceptedBatchBasedOnCampaignId}
          openModal={openModalToUpdateBatch}
          setOpenModalToUpdateBatch={setOpenModalToUpdateBatch}
          batch={detailsOfBatchSelected}
          setAvailableBatches={setAvailableBatches}
          fetchAllJobsBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          fetchAllBatchesBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          // Pass the new props
          setIsNotAccepted={setIsNotAccepted}
          setAcceptedBatches={setAcceptedBatches}
          onBatchUpdate={handleBatchUpdate}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedJob?.status != 2 && (
          <MenuItem
            onClick={() => {
              handleOpenRejectModal(selectedJob);
            }}
            disabled={isProcessing || isRejectProcessing}
          >
            {isProcessing || isRejectProcessing ? "Processing..." : "Reject Job"}
          </MenuItem>
        )}
      </Menu>

      <RejectModal
        open={rejectDialogOpen}
        onClose={closeRejectModal}
        onConfirm={handleRejectConfirm}
        title="Reject Job"
        isProcessing={isRejectProcessing}
        confirmButtonText="Reject"
        cancelButtonText="Cancel"
        placeholder="Enter rejection reason..."
        rows={4}
        itemName={itemInfo.name}
        itemCount={itemInfo.count}
      />
    </Grid>
  );
};

export default NotAccepted;
